import { defineStore } from 'pinia';
import { getDefaultAnalyticsDates } from '@/utils/date';
import { workGapStatsApi } from '@/services/workGapStatsService';

const { defaultStartDate, defaultEndDate } = getDefaultAnalyticsDates();

export const useWorkGapStore = defineStore('workgap', {
  state: () => ({
    searchLimit: 250,
    searchPage: 1,
    searchDirection: 'asc',
    searchField: '',
    totalRows: 0,
    detailsTableData: [],

    startDate: defaultStartDate,
    endDate: defaultEndDate,
    defaultStartDate: defaultStartDate,
    defaultEndDate: defaultEndDate,

    lastUpdated: '',

    selectedUsers: [],
    selectedActivityGap: [],
    filtersApplied: [],
    previousMonth: '',
    lastMonth: '',
    sixMonthRange: '',
    monthCount: 0,
    monthlyTotal: 0,
    sixMonthAvg: 0,
    monthlyVS6MonthAverageCount: {
      value: 0,
      colorTernary: 0,
      caretTernary: 0,
    },
    monthlyVS6MonthAveragePercent: {
      value: 0,
      colorTernary: 0,
      caretTernary: 0,
    },
    monthlyInsights: [],
    highOccurrenceDays: [],
    mostOccurrenceUsers: [],

    //breakout table
    breakoutPageLimit: 250,
    breakoutPageNumber: 1,
    breakoutSearchDirection: 'asc',
    breakoutSearchField: 'name',
    breakoutTotalRows: 0,
    breakoutHeaders: [],

    splitBy: { text: 'None', id: 'none' },

    //breakout chart
    breakoutChart: {},

    isLoading: false,

    lastFetchFingerprint: null,
  }),
  getters: {
    getFiltersApplied: state => {
      return state.filtersApplied;
    },
  },
  actions: {
    resetFilters() {
      this.selectedUsers = [];
      this.selectedActivityGap = [];

      this.startDate = defaultStartDate;
      this.endDate = defaultEndDate;
    },
    async clearFilter(filter) {
      if (filter === 'Date Range Applied') {
        this.startDate = defaultStartDate;
        this.endDate = defaultEndDate;
        this.breakoutSearchField = 'name';
      } else if (filter === 'Users') {
        this.selectedUsers = [];
      } else if (filter === 'Activity Gap') {
        this.selectedActivityGap = [];
      }

      await this.applyFilters(
        this.startDate,
        this.endDate,
        this.selectedUsers,
        this.selectedActivityGap
      );
    },
    async applyFilters(startDate, endDate, selectedUsers, selectedActivityGap) {
      const incomingFingerprint = JSON.stringify([
        startDate,
        endDate,
        selectedUsers,
        selectedActivityGap,
      ]);

      if (incomingFingerprint === this.lastFetchFingerprint) {
        return;
      }

      this.startDate = startDate;
      this.endDate = endDate;
      this.selectedUsers = selectedUsers;
      this.selectedActivityGap = selectedActivityGap;
      this.searchPage = 1;
      this.breakoutPageNumber = 1;
      this.filtersApplied = [];

      if (startDate !== defaultStartDate || endDate !== defaultEndDate) {
        this.filtersApplied.push('Date Range Applied');
      }

      if (selectedUsers && selectedUsers.length > 0) {
        this.filtersApplied.push('Users');
      }

      if (selectedActivityGap && selectedActivityGap.length > 0) {
        this.filtersApplied.push('Activity Gap');
      }

      await Promise.all([
        this.fetchMetrics(),
        this.fetchBreakoutChart(),
        this.fetchBreakoutTable(),
        this.fetchDetails(),
        this.fetchLastUpdated(),
      ]);

      this.lastFetchFingerprint = incomingFingerprint;
    },
    async fetchBreakoutChart(splitBy = this.splitBy) {
      this.splitBy = splitBy;
      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      const response = await workGapStatsApi.getBreakoutChart(payload);
      if (splitBy.id === 'user') {
        response.series = response.series.map(item => ({
          ...item,
          name: item.first_name,
        }));
      }
      this.breakoutChart.categories = response.categories;
      this.breakoutChart.series = response.series;
    },
    async fetchBreakoutTable(
      splitBy = this.splitBy,
      page = this.breakoutPageNumber,
      limit = this.breakoutPageLimit,
      direction = this.breakoutSearchDirection,
      field = this.breakoutSearchField
    ) {
      if (splitBy.id === 'none') {
        return;
      }
      this.breakoutPageNumber = page;
      this.breakoutPageLimit = limit;
      this.breakoutSearchDirection = direction;
      this.breakoutSearchField = field;

      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      payload.pagination = this._buildBreakoutPagination(
        page,
        limit,
        direction,
        field
      );
      const response = await workGapStatsApi.getBreakoutTable(payload);
      this.breakoutChart.sortableSeries = response.sortableSeries;
      this.breakoutTotalRows = response.totalData;
      this.updateHeaders();
    },
    async fetchMetrics() {
      this.isLoading = true;
      try {
        const payload = this._buildPayload();
        await Promise.all([
          this._fetchAverageTotalInsights(payload),
          this._fetchMonthlyInsights(payload),
          this._fetchHighOccurrenceDays(payload),
          this._fetchMostOccurrences(payload),
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDetails() {
      this.isLoading = true;
      const payload = this._buildPayload();
      //janky lil fix because gap corresponds to formatted time in the table
      // Formatted time is a varchar so it sorts alphabetically,
      // and work gap mins is an integer so sorting is correct now
      if (this.searchField == 'gap') {
        this.searchField = 'workgap_mins';
      }
      const pagination = {
        pageNumber: this.searchPage,
        pageLimit: this.searchLimit,
        sortField: this.searchField,
        sortOrder: this.searchDirection,
      };
      payload.pagination = pagination;
      const response = await workGapStatsApi.getDetails(payload);

      this.detailsTableData = response.data;
      this.totalRows = response.total_records;
      this.isLoading = false;
    },
    async fetchLastUpdated() {
      const response = await workGapStatsApi.getLastUpdated();
      this.lastUpdated = response.last_updated_timestamp;
    },
    updateHeaders() {
      this.breakoutHeaders = [
        {
          key: 'name',
          value: this.splitBy.text,
          width: '16rem',
          sort: true,
        },
        ...this.breakoutChart.categories.map(category => {
          const fullCategory = category.join(' ');
          return {
            key: fullCategory,
            value: fullCategory,
            width: '16rem',
            sort: true,
          };
        }),
        {
          key: 'total',
          value: 'Total',
          width: '16rem',
          sort: true,
        },
      ];
    },
    get_pagination() {
      return {
        sortField: this.searchField,
        sortOrder: this.searchDirection,
      };
    },
    // Helper methods
    _buildPayload() {
      return {
        filter: {
          startDate: this.startDate,
          endDate: this.endDate,
          auxoUsers: this.selectedUsers.length
            ? this.selectedUsers.map(user => user.id)
            : [],
          activityGap: this.selectedActivityGap.length
            ? this.selectedActivityGap.map(activity => activity.id)
            : [],
        },
      };
    },
    _buildBreakoutPagination(page, limit, direction, field) {
      this.breakoutPageLimit = limit;
      this.breakoutPageNumber = page;
      this.breakoutSearchDirection = direction;
      this.breakoutSearchField = field;
      return {
        pageLimit: limit,
        pageNumber: page,
        sortOrder: direction,
        sortField: field,
      };
    },

    async _fetchAverageTotalInsights(payload) {
      const response = await workGapStatsApi.getAverageTotalInsights(payload);

      this.previousMonth = response.previous_month;
      this.monthlyTotal = response.previous_month_count;
      this.sixMonthAvg = Math.round(response.six_month_average * 10) / 10;
      this.sixMonthRange = response.date_range;

      // Count comparison
      this.monthCount = response.month_count;
      this.monthlyVS6MonthAverageCount.value = Math.abs(
        Math.round(response.difference * 10) / 10
      );
      this.monthlyVS6MonthAverageCount.colorTernary =
        response.difference > 0 ? 1 : 2;
      this.monthlyVS6MonthAverageCount.caretTernary =
        response.difference > 0 ? 1 : 2;

      // Percentage comparison
      this.monthlyVS6MonthAveragePercent.value = Math.abs(
        Math.round(response.percent_difference * 10) / 10
      );
      this.monthlyVS6MonthAveragePercent.colorTernary =
        response.percent_difference > 0 ? 1 : 2;
      this.monthlyVS6MonthAveragePercent.caretTernary =
        response.percent_difference > 0 ? 1 : 2;

      this.lastMonth = response.previous_month;
    },

    async _fetchMonthlyInsights(payload) {
      const response = await workGapStatsApi.getMonthlyInsights(payload);

      this.monthlyInsights = response.map(item => this._formatMonthlyInsight(item));
    },

    _formatMonthlyInsight(item) {
      return {
        key: item.type,
        value: item.total_count,
        textValue: item.activity_month,
        labelText: `${
          item.type.charAt(0).toUpperCase() + item.type.slice(1)
        } Month:`,
        colorTernary:
          item.type === 'current' ? 0 : item.type === 'highest' ? 1 : 2,
        showWarning: item.type === 'current' && this.filtersApplied.includes('Date Range Applied'),
      };
    },

    async _fetchHighOccurrenceDays(payload) {
      const response = await workGapStatsApi.getHighOccurrenceDays(payload);

      this.highOccurrenceDays = response.map(item => ({
        value: item.total_count,
        textValue: item.weekday,
      }));
    },

    async _fetchMostOccurrences(payload) {
      const response = await workGapStatsApi.getMostOccurrences(payload);

      this.mostOccurrenceUsers = response.map(item => ({
        user: `${item.first_name} ${item.last_name}`,
        smallHours: item.short_gap_count,
        largeHours: item.long_gap_count,
        total: item.total_count,
      }));
    },
  },
});
